import * as z from 'zod'

import {
  CreateAnalysisTargetVars,
  UpdateAnalysisTargetVars
} from '@/common/api/params/mediaAnalysisTarget'
import { NameLabelMapping } from '@/common/app/message'
import { MAX_COMPETITORS } from '@/common/app/modules/media/shareOfVoice'
import { StringLength } from '@/common/form/schema'
import { MediaAnalysisTarget } from '@/common/model/media/mediaAnalysisTarget'

const startsWithHttp = (v: string) => !v || v.startsWith('http://') || v.startsWith('https://')

// 競合ドメインは最大3件まで
export const MAX_COMPETITOR_DOMAINS = 3

const competitorBrandSchema = z.object({
  brand: z.string().max(StringLength.NORMAL_MAX),
  url: z.string().max(StringLength.NORMAL_MAX)
})

export const analysisTargetSchema = z
  .object({
    id: z.number().readonly().optional(),
    siteId: z.number().readonly(),
    name: z.string().min(1).max(StringLength.NORMAL_MAX),
    siteUrl: z.string().min(1).max(2000).url('正しいURLを入力してください'),
    keywordInclude: z.string().max(StringLength.NORMAL_MAX).optional(),
    keywordExclude: z.string().max(StringLength.NORMAL_MAX).optional(),
    competitorDomains: z.string().max(StringLength.NORMAL_MAX).array(),
    // AI検索最適化（GEO/LLMO）用（site.enable_geo_llmo が有効な場合のみフォームに表示）
    ownBrand: z.string().max(StringLength.NORMAL_MAX).optional(),
    ownUrl: z
      .string()
      .max(StringLength.NORMAL_MAX)
      .refine(startsWithHttp, { message: 'URLはhttp://またはhttps://で始めてください' })
      .optional(),
    category: z.string().max(StringLength.NORMAL_MAX).optional(),
    competitorBrands: z.array(competitorBrandSchema).max(MAX_COMPETITORS)
  })
  .superRefine((data, ctx) => {
    data.competitorBrands.forEach((c, i) => {
      if (c.url.trim() && !startsWithHttp(c.url.trim())) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['competitorBrands', i, 'url'],
          message: 'URLはhttp://またはhttps://で始めてください'
        })
      }
    })
  })

export type AnalysisTargetFormSchemaType = z.infer<typeof analysisTargetSchema>

// 入力のある競合ドメインのみ抽出
const filledCompetitorDomains = (data: AnalysisTargetFormSchemaType) =>
  data.competitorDomains.map((d) => d.trim()).filter(Boolean)

// 入力のある競合ブランドのみ抽出
const filledCompetitorBrands = (data: AnalysisTargetFormSchemaType) =>
  data.competitorBrands.filter((c) => c.brand.trim() || c.url.trim())

export const convertAnalysisTargetSchemaToVars = (
  schema: AnalysisTargetFormSchemaType
): UpdateAnalysisTargetVars | CreateAnalysisTargetVars => {
  const competitors = filledCompetitorBrands(schema)
  const base = {
    name: schema.name,
    site_url: schema.siteUrl,
    keyword_include: schema.keywordInclude ?? '',
    keyword_exclude: schema.keywordExclude ?? '',
    competitor_domains: filledCompetitorDomains(schema),
    own_brand: schema.ownBrand ?? '',
    own_url: schema.ownUrl ?? '',
    category: schema.category ?? '',
    competitor_brands: competitors.map((c) => c.brand.trim()),
    competitor_urls: competitors.map((c) => c.url.trim())
  }
  if (schema.id !== undefined) {
    return { site_id: schema.siteId, target_id: schema.id, ...base }
  }
  return { site_id: schema.siteId, ...base }
}

export const convertAnalysisTargetToSchema = (
  target: MediaAnalysisTarget
): AnalysisTargetFormSchemaType => {
  const brands = target.competitor_brands ?? []
  const urls = target.competitor_urls ?? []
  const competitorBrands =
    brands.length > 0
      ? brands.map((b, i) => ({ brand: b, url: urls[i] ?? '' }))
      : [{ brand: '', url: '' }]

  return {
    id: target.id,
    siteId: target.site_id,
    name: target.name,
    siteUrl: target.site_url,
    keywordInclude: target.keyword_include ?? '',
    keywordExclude: target.keyword_exclude ?? '',
    // 入力欄の数だけ必ず値を埋める（疎配列にすると入力へ反映されず、保存時の検証も通らない）
    competitorDomains: Array.from(
      { length: MAX_COMPETITOR_DOMAINS },
      (_, i) => target.competitor_domains?.[i] ?? ''
    ),
    ownBrand: target.own_brand ?? '',
    ownUrl: target.own_url ?? '',
    category: target.category ?? '',
    competitorBrands
  }
}

export const makeDefaultAnalysisTargetSchema = (siteId: number): AnalysisTargetFormSchemaType => ({
  siteId,
  name: '',
  siteUrl: '',
  keywordInclude: '',
  keywordExclude: '',
  competitorDomains: Array(MAX_COMPETITOR_DOMAINS).fill(''),
  ownBrand: '',
  ownUrl: '',
  category: '',
  competitorBrands: [{ brand: '', url: '' }]
})

export const analysisTargetNameLabelMapping: NameLabelMapping = {
  name: '分析対象サイト名',
  siteUrl: 'サイトURL',
  keywordInclude: 'キーワードフィルタ（含む）',
  keywordExclude: 'キーワードフィルタ（含まない）',
  competitorDomains: '競合ドメイン',
  ownBrand: '自社ブランド名',
  ownUrl: '自社ブランドURL',
  category: '商品/サービスカテゴリ',
  competitorBrands: '競合ブランド'
}
